import stockadjustmentService from '../../../services/stockadjustmentService';
import filterService from '../../../services/filterService';

import {
    setStockAdjustmentLoading,
    setStockAdjustmentError,
    setStockAdjustmentList,
    setStockAdjustmentCurrent,
    setStockAdjustmentPagination,
    setStockAdjustmentSort,
    setStockAdjustmentFilters,
    setStockAdjustmentDefaultFilter,
    setStockAdjustmentSettings,
    clearStockAdjustmentCurrent,
    clearStockAdjustmentError,
    setStockAdjustmentFilterLoading,
    changeStockAdjustmentFilterCondition,
    addStockAdjustmentFilterCondition,
    updateStockAdjustmentFilterConditionRule,
    updateStockAdjustmentFilterConditionValue,
    removeStockAdjustmentFilterCondition,
    setStockAdjustmentSelected,
    resetStockAdjustmentFiltersAndPagination,
    setStockAdjustmentClassicFilters,
    setStockAdjustmentVisibleColumns,
} from './stockadjustmentSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        if (!getState().stockadjustment.isSettingsInitialized) {
            dispatch(setStockAdjustmentLoading(true));
            const response = await stockadjustmentService.getSettings();

            dispatch(setStockAdjustmentSettings(response));

            const classicFilters = response?.classic_filters || {};
            dispatch(setStockAdjustmentClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setStockAdjustmentDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setStockAdjustmentError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setStockAdjustmentLoading(false));
    }
};

export const fetchStockadjustments = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        const state = getState().stockadjustment;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setStockAdjustmentLoading(true));
        const response = await stockadjustmentService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setStockAdjustmentList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchStockadjustments:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setStockAdjustmentError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setStockAdjustmentLoading(false));
    }
};

export const fetchStockAdjustment = (id) => async (dispatch) => {
    try {
        dispatch(setStockAdjustmentLoading(true));
        const response = await stockadjustmentService.getOne(id);
        dispatch(setStockAdjustmentCurrent(response.data));
    } catch (error) {
        dispatch(setStockAdjustmentError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setStockAdjustmentLoading(false));
    }
};

export const createStockAdjustment = (data) => async (dispatch) => {
    try {
        const response = await stockadjustmentService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateStockAdjustment = (id, data) => async (dispatch) => {
    try {
        const response = await stockadjustmentService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteStockAdjustment = (id) => async (dispatch) => {
    try {
        dispatch(setStockAdjustmentLoading(true));
        const response = await stockadjustmentService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setStockAdjustmentError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setStockAdjustmentLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setStockAdjustmentPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setStockAdjustmentSort(sort));
};

export const searchStockadjustments = (query) => (dispatch, getState) => {
    const currentFilters = getState().stockadjustment.filters;
    dispatch(setStockAdjustmentFilters({ ...currentFilters, search: query }));
};

export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setStockAdjustmentDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeStockAdjustmentFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addStockAdjustmentFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateStockAdjustmentFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateStockAdjustmentFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeStockAdjustmentFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setStockAdjustmentFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().stockadjustment.settings;
            dispatch(setStockAdjustmentSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setStockAdjustmentFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setStockAdjustmentFilterLoading(false));
        dispatch(setStockAdjustmentError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setStockAdjustmentSelected(selectedRows));
    };
};

export const clearCurrentStockAdjustment = () => {
    return (dispatch) => {
        dispatch(clearStockAdjustmentCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetStockAdjustmentFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().stockadjustment.classicFilters;
    const updatedClassicFilters = { ...currentClassicFilters };

    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value }),
            },
        };
    }

    dispatch(setStockAdjustmentClassicFilters(updatedClassicFilters));
};

export const setClassicFilterDateRange = (fromDate, toDate) => (dispatch, getState) => {
    const currentClassicFilters = getState().stockadjustment.classicFilters;
    const updatedClassicFilters = { ...currentClassicFilters };

    if (updatedClassicFilters.from_date) {
        updatedClassicFilters.from_date = {
            ...updatedClassicFilters.from_date,
            query: {
                ...updatedClassicFilters.from_date.query,
                value: fromDate,
            },
        };
    }

    if (updatedClassicFilters.to_date) {
        updatedClassicFilters.to_date = {
            ...updatedClassicFilters.to_date,
            query: {
                ...updatedClassicFilters.to_date.query,
                value: toDate,
            },
        };
    }

    dispatch(setStockAdjustmentClassicFilters(updatedClassicFilters));
};

export const setVisibleColumns = (columns) => (dispatch) => {
    dispatch(setStockAdjustmentVisibleColumns(columns));
};

export const applyStockAdjustmentListFilters = (params = {}) => async (dispatch, getState) => {
    const { pagination } = getState().stockadjustment;
    dispatch(setStockAdjustmentPagination({ ...pagination, current_page: 1 }));
    return dispatch(fetchStockadjustments({ current_page: 1, ...params }));
};

export const setStockAdjustmentSortAndFetch = (sort) => async (dispatch, getState) => {
    dispatch(setStockAdjustmentSort(sort));
    const { pagination } = getState().stockadjustment;
    dispatch(setStockAdjustmentPagination({ ...pagination, current_page: 1 }));
    return dispatch(fetchStockadjustments({
        current_page: 1,
        sort_field: sort.field,
        sort_direction: sort.direction,
    }));
};
